import {
  afterRenderEffect,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  ElementRef,
  inject,
  input,
  linkedSignal,
  untracked,
  viewChildren,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DatePipe } from '@angular/common';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { ProviderTokenService } from '../../services/provider-token.service';
import { ProviderTokenStatusService } from '../../services/provider-token-status.service';
import { ProviderTokenProjectRef, ProviderTokenSlot, ProviderType } from '../../models/provider-token.model';

interface RowState {
  slot: ProviderTokenSlot;
  expanded: boolean;
  draft: string;
  busy: boolean;
}

@Component({
  selector: 'app-provider-tokens-section',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [DatePipe, TranslatePipe, MatIcon, MatProgressSpinner],
  templateUrl: './provider-tokens-section.component.html',
  styleUrl: './provider-tokens-section.component.scss',
})
export class ProviderTokensSectionComponent {
  private readonly service = inject(ProviderTokenService);
  private readonly status = inject(ProviderTokenStatusService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly snackBar = inject(MatSnackBar);
  private readonly translate = inject(TranslateService);
  private readonly zeilen = viewChildren<ElementRef<HTMLElement>>('zeile');
  private gescrollt = false;

  readonly fokusHost = input<string | null>(null);

  readonly rows = linkedSignal<ProviderTokenSlot[], RowState[]>({
    source: this.status.slots,
    computation: (slots, vorher) => {
      const bisher = new Map((vorher?.value ?? []).map((row) => [slotKey(row.slot), row]));
      return slots.map((slot) => {
        const row = bisher.get(slotKey(slot));
        if (row) return { ...row, slot, busy: false };
        return { slot, expanded: !slot.tokenSet || untracked(() => this.istFokussiert(slot)), draft: '', busy: false };
      });
    },
  });

  readonly loading = computed(() => this.status.status() === 'laedt' && this.rows().length === 0);
  readonly loadError = computed(() => this.status.status() === 'fehler');
  readonly missingCount = computed(() => this.rows().filter((r) => !r.slot.tokenSet).length);
  readonly hasSlots = computed(() => this.rows().length > 0);
  private readonly fokusZeile = computed(() => this.rows().find((r) => this.istFokussiert(r.slot)) ?? null);

  constructor() {
    afterRenderEffect(() => {
      const ziel = this.fokusZeile();
      const elemente = this.zeilen();
      if (this.gescrollt || !ziel) return;
      const element = elemente.find((e) => e.nativeElement.getAttribute('data-host') === ziel.slot.host);
      if (!element) return;
      this.gescrollt = true;
      element.nativeElement.scrollIntoView({ block: 'center' });
    });
  }

  istFokussiert(slot: ProviderTokenSlot): boolean {
    const fokus = this.fokusHost()?.trim().toLowerCase();
    return !!fokus && slot.host.toLowerCase() === fokus;
  }

  retry(): void {
    this.status.laden();
  }

  toggleRow(row: RowState): void {
    this.updateRow(row, (r) => ({ ...r, expanded: !r.expanded, draft: '' }));
  }

  onDraftInput(row: RowState, value: string): void {
    this.updateRow(row, (r) => ({ ...r, draft: value }));
  }

  save(row: RowState): void {
    const token = row.draft.trim();
    if (!token || row.busy) return;
    this.updateRow(row, (r) => ({ ...r, busy: true }));
    this.service
      .upsert({ providerType: row.slot.providerType, host: row.slot.host, apiToken: token })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.updateRow(row, (r) => ({ ...r, expanded: false, draft: '' }));
          this.erfolg('providerTokens.snack.saved');
        },
        error: () => this.fehlschlag(row),
      });
  }

  remove(row: RowState): void {
    if (row.busy) return;
    this.updateRow(row, (r) => ({ ...r, busy: true }));
    this.service
      .delete(row.slot.providerType, row.slot.host)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.updateRow(row, (r) => ({ ...r, draft: '' }));
          this.erfolg('providerTokens.snack.deleted');
        },
        error: () => this.fehlschlag(row),
      });
  }

  providerLabel(providerType: ProviderType): string {
    return providerType.toLowerCase();
  }

  joinProjectNames(projects: ProviderTokenProjectRef[]): string {
    const shown = projects.slice(0, 3).map((p) => p.name);
    const suffix = projects.length > 3 ? ' …' : '';
    return shown.join(' · ') + suffix;
  }

  projectsKey(projects: ProviderTokenProjectRef[]): string {
    return projects.length === 1 ? 'providerTokens.projectsOne' : 'providerTokens.projectsOther';
  }

  providerHelpKey(providerType: ProviderType): string {
    return `providerTokens.help.${providerType.toLowerCase()}`;
  }

  actionLabelKey(row: RowState): string {
    if (row.expanded) return 'providerTokens.action.close';
    return row.slot.tokenSet ? 'providerTokens.action.rotate' : 'providerTokens.action.set';
  }

  actionAriaKey(row: RowState): string {
    return row.slot.tokenSet ? 'providerTokens.action.ariaRotate' : 'providerTokens.action.ariaSet';
  }

  private erfolg(textKey: string): void {
    this.snackBar.open(this.translate.instant(textKey), 'OK', { duration: 3000 });
    this.status.laden();
  }

  private fehlschlag(row: RowState): void {
    this.updateRow(row, (r) => ({ ...r, busy: false }));
    this.snackBar.open(this.translate.instant('providerTokens.snack.error'), 'OK', { duration: 5000 });
  }

  private updateRow(row: RowState, aenderung: (row: RowState) => RowState): void {
    const key = slotKey(row.slot);
    this.rows.update((rows) => rows.map((r) => (slotKey(r.slot) === key ? aenderung(r) : r)));
  }
}

function slotKey(slot: ProviderTokenSlot): string {
  return `${slot.providerType}|${slot.host}`;
}
